import { PROJECT } from './config.generated';

export type PriceTier = { fromUnit: number; toUnit: number | null; cents: number };
export type StorePackage = { units: number; displayCents: number };

/**
 * Single source of truth for prices: project.config.json → config.generated.ts.
 * Web (Stripe graduated price), landing, paywall and server all derive from these constants.
 * Store prices live in App Store/Play (shown from RevenueCat); STORE_PACKAGES only lists the package sizes
 * plus reference prices for docs and store maintenance.
 */
export const PRICE_TIERS: readonly PriceTier[] = PROJECT.pricing.tiersCentsPerUnitYear;
export const FREE_UNITS: number = PROJECT.pricing.freeUnits;
export const STORE_PACKAGES: readonly StorePackage[] = PROJECT.storePackages.packages;
/** Product id of a store package (IMMUTABLE pattern, docs/IMMUTABLE.md). */
export const storeProductId = (units: number) => PROJECT.storePackages.productIdPattern.replace('<n>', String(units));
/**
 * KI-Assistent as an add-on for the whole account (docs/BILLING.md §10, decided 2026-10-21): grants only the assistant,
 * never units. Web: fixed Stripe price STRIPE_AI_PRICE_ID (quantity 1), as a second item of the units subscription or
 * alone. Store: product `productId` in its own subscription group (runs next to a unit package), entitlement `ai`.
 */
export const AI_ADDON = PROJECT.aiAddon;
/** Store product of the add-on: `immoaktas_ai_yearly` (App Store) or `immoaktas_ai_yearly:<basePlan>` (Play). */
export const isAiAddonProduct = (product?: string | null) => !!product && (product === AI_ADDON.productId || product.startsWith(`${AI_ADDON.productId}:`));
/** Gross yearly price of the add-on: web (Stripe) or store (App Store/Play reference price). */
export const aiAddonCents = (source?: string | null) => (source === 'revenuecat' ? AI_ADDON.storePriceCentsYear : AI_ADDON.webPriceCentsYear);
/**
 * Legacy Premium (2026-10-16 to 2026-10-21, no longer sold): 119 € a year with `includedUnits` units and the assistant.
 * Running purchases keep granting both until they end (Stripe STRIPE_PREMIUM_PRICE_ID, store `productId`, entitlement `premium`).
 */
export const PREMIUM = PROJECT.premium;
/** Store product of legacy Premium: `immoaktas_premium_yearly` (App Store) or `immoaktas_premium_yearly:<basePlan>` (Play). */
export const isPremiumProduct = (product?: string | null) => !!product && (product === PREMIUM.productId || product.startsWith(`${PREMIUM.productId}:`));

/** Reference yearly price (cents) of the store package with exactly `units` units; null when no package has that size. */
export const storePackageCents = (units: number): number | null => STORE_PACKAGES.find((p) => p.units === units)?.displayCents ?? null;

/**
 * Yearly price in cents for n billable units: units up to FREE_UNITS are free, every further unit costs the
 * price of the tier it falls into (graduated, like a Stripe "graduated" price). Works for any number of tiers.
 */
export function yearlyPriceCents(n: number, tiers: readonly PriceTier[] = PRICE_TIERS): number {
  if (!Number.isInteger(n) || n < 0) throw new Error('units must be a non-negative integer');
  let total = 0;
  for (const t of tiers) {
    const upper = t.toUnit ?? Number.POSITIVE_INFINITY;
    const count = Math.max(0, Math.min(n, upper) - (t.fromUnit - 1));
    total += count * t.cents;
  }
  return total;
}

/** Tiers must start right after the free units, be contiguous and end open (toUnit null). Returns problems. */
export function validateTiers(tiers: readonly PriceTier[] = PRICE_TIERS, freeUnits = FREE_UNITS): string[] {
  const errors: string[] = [];
  if (!tiers.length) return ['no tiers'];
  if (tiers[0]!.fromUnit !== freeUnits + 1) errors.push(`first tier must start at ${freeUnits + 1}`);
  tiers.forEach((t, i) => {
    const next = tiers[i + 1];
    if (t.toUnit !== null && t.toUnit < t.fromUnit) errors.push(`tier ${i} ends before it starts`);
    if (next && t.toUnit !== next.fromUnit - 1) errors.push(`gap/overlap after tier ${i}`);
    if (!next && t.toUnit !== null) errors.push('last tier must be open (toUnit null)');
  });
  return errors;
}

/** Cheapest per-unit price (the open last tier) – e.g. "ab x € je Wohnung". */
export const lowestTierCents = (tiers: readonly PriceTier[] = PRICE_TIERS) => Math.min(...tiers.map((t) => t.cents));
/**
 * Monthly equivalent of a yearly price in cents, rounded UP so "ab x € pro Wohnung und Monat" never understates
 * (e.g. 490 → 41 = "ab 0,41 €").
 */
export const monthlyCents = (yearlyCents: number) => Math.ceil(yearlyCents / 12);
/** Cheapest monthly price of one paid unit ("ab 0,41 € pro Wohnung und Monat"). */
export const lowestMonthlyPerUnitCents = (tiers: readonly PriceTier[] = PRICE_TIERS) => monthlyCents(lowestTierCents(tiers));
/** First unit of the open last tier (e.g. 51). */
export const openTierFrom = (tiers: readonly PriceTier[] = PRICE_TIERS) => tiers[tiers.length - 1]!.fromUnit;

/**
 * Builds a human-readable tier list. Wording stays with the caller (i18n keys or page copy), numbers come from
 * the tiers: `range` for closed tiers, `open` for the last one, `price` formats cents.
 */
export function describeTiers(
  fmt: { range: (from: number, to: number, price: string, index: number) => string; open: (from: number, price: string, index: number) => string; price: (cents: number) => string },
  tiers: readonly PriceTier[] = PRICE_TIERS,
  separator = ', ',
): string {
  return tiers.map((t, i) => (t.toUnit === null ? fmt.open(t.fromUnit, fmt.price(t.cents), i) : fmt.range(t.fromUnit, t.toUnit, fmt.price(t.cents), i))).join(separator);
}

export type YearlyEstimate = { cents: number; /** Which price list produced `cents`: store package reference price or web tiers. */ basis: 'store' | 'web' };
/**
 * Estimated yearly revenue of ONE paid subscription (admin revenue dashboard). Subscriptions bought in the app stores
 * (`billing.source === 'revenuecat'`) are priced with the store package of that size (STORE_PACKAGES), everything else
 * (Stripe, unknown source, or a store size without a reference price) with the web tiers. Estimate only: gross prices,
 * no store commission, no VAT, no proration. `premium` = legacy Premium plan; the AI add-on is a separate subscription
 * (`aiAddonCents`) that the caller adds.
 */
export function estimateYearlyCents(units: number, source?: string | null, premium = false): YearlyEstimate {
  // legacy Premium had one price everywhere (store and web); its units are included
  if (premium) return { cents: PREMIUM.priceCentsYear, basis: source === 'revenuecat' ? 'store' : 'web' };
  if (source === 'revenuecat') {
    const cents = storePackageCents(units);
    if (cents !== null) return { cents, basis: 'store' };
  }
  return { cents: yearlyPriceCents(units), basis: 'web' };
}

/**
 * Volume discount of the graduated price: what n units would cost if every paid unit had the price of the first
 * paid tier, minus the real graduated price ("Du sparst X € pro Jahr gegenüber dem Einzelpreis"). 0 up to the end of
 * the first tier.
 */
export function volumeSavingsCents(units: number, tiers: readonly PriceTier[] = PRICE_TIERS, freeUnits: number = FREE_UNITS): number {
  const n = yearlyPriceCents(units, tiers);
  const single = Math.max(0, units - freeUnits) * tiers[0]!.cents;
  return Math.max(0, single - n);
}

/** From this many units, "Angebot anfordern" is offered prominently (personal offer for big portfolios). */
export const QUOTE_FROM_UNITS: number = PROJECT.pricing.quoteFromUnits;
/** Smallest and largest store package size (e.g. "Jahrespakete von 2 bis 50 Wohnungen"). */
export const storePackageRange = (packages: readonly StorePackage[] = STORE_PACKAGES) => {
  const sizes = packages.map((p) => p.units);
  return { min: Math.min(...sizes), max: Math.max(...sizes) };
};
/** Largest size up to which every package size exists, counted from the smallest package (e.g. 10 for 2…10, 15, 20). */
export function storeExactUpTo(packages: readonly StorePackage[] = STORE_PACKAGES): number {
  const sizes = new Set(packages.map((p) => p.units));
  let n = storePackageRange(packages).min;
  while (sizes.has(n + 1)) n++;
  return n;
}

/** mailto: link to request a personal offer; subject and body come from the caller's i18n texts. */
export const quoteMailto = (subject: string, body: string, to: string = PROJECT.contact.hello) =>
  `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
